import React, { useState } from "react";
import {
  Formik,
  Field,
  Form,
  ErrorMessage as FormikErrorMessage,
} from "formik";
import { useDispatch } from "react-redux";
import { createNotificationAsync } from "../../redux/slices/notificationsSlice";
import BackButton from "../../components/BackButton";
import Header from "../../components/Header";
import { notificationValidations } from "../../validations/notificationValidations";
import { AppDispatch } from "../../redux/store";
import SuccessMessage from "../../components/SuccessMessage";
import ErrorMessage from "../../components/ErrorMessage";
import { useNavigate } from "react-router";

const AddNotification: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // const formatDate = (dateString: string) => {
  //     const [year, month, day] = dateString.split('-');
  //     return `${day}/${month}/${year}`;
  // };

  const getTodayDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getCurrentTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const convertToUTC = (date: string, time: string) => {
    const localDateTime = new Date(`${date}T${time}`);
    return localDateTime.toISOString();
  };

  return (
    <div className="p-6 bg-gray-200 min-h-screen flex flex-col">
      <Header title="Add Notification" />
      <div className="mb-4">
        <BackButton onClick={() => window.history.back()} />
      </div>
      <div className="max-w-md w-full mx-auto bg-gray-100 p-6 rounded-lg shadow-md">
        <Formik
          initialValues={{
            title: "",
            content: "",
            date: "",
            time: "",
            repeat: "",
          }}
          validationSchema={notificationValidations}
          validate={(values) => {
            const errors: Record<string, string> = {};
            const today = getTodayDate();
            const currentTime = getCurrentTime();

            if (values.date === today && values.time < currentTime) {
              errors.time = "Time cannot be earlier than the current time!";
            }

            return errors;
          }}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            const formattedValues = {
              ...values,
              date: convertToUTC(values.date, values.time),
            };

            try {
              await dispatch(createNotificationAsync(formattedValues)).unwrap();
              setSuccessMessage("Notification added successfully!");
              resetForm();
              navigate("/notifications");
            } catch (error) {
              setErrorMessage(`Error: ${(error as Error).message}`);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">
                  Notification Title
                </label>
                <Field
                  name="title"
                  type="text"
                  maxLength={50}
                  placeholder="Enter notification title"
                  className="w-full px-3 py-2 border rounded-md focus:ring focus:ring-blue-300"
                />
                {errors.title && touched.title && (
                  <div className="text-red-500 text-sm mt-1">
                    {errors.title}
                  </div>
                )}
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">
                  Notification Content
                </label>
                <Field
                  name="content"
                  as="textarea"
                  maxLength={1000}
                  placeholder="Enter notification content"
                  className="w-full px-3 py-2 border rounded-md focus:ring focus:ring-blue-300"
                />
                {errors.content && touched.content && (
                  <div className="text-red-500 text-sm mt-1">
                    {errors.content}
                  </div>
                )}
              </div>
              <div className="flex gap-4 mb-4">
                <div className="w-1/2">
                  <label className="block text-gray-700 mb-2">Date</label>
                  <Field
                    name="date"
                    type="date"
                    min={new Date().toISOString().split("T")[0]}
                    className="w-full px-3 py-2 border rounded-md focus:ring focus:ring-blue-300"
                  />
                  {errors.date && touched.date && (
                    <div className="text-red-500 text-sm mt-1">
                      {errors.date}
                    </div>
                  )}
                </div>
                <div className="w-1/2">
                  <label className="block text-gray-700 mb-2">Time</label>
                  <Field
                    name="time"
                    type="time"
                    className="w-full px-3 py-2 border rounded-md focus:ring focus:ring-blue-300"
                  />
                  <FormikErrorMessage
                    name="time"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-gray-700 mb-2">Repeat</label>
                <Field
                  name="repeat"
                  as="select"
                  className="w-full px-3 py-2 border rounded-md focus:ring focus:ring-blue-300"
                >
                  <option value="">Clear</option>
                  <option value="Daily">Daily</option>
                  <option value="Weekdays">Weekdays</option>
                  <option value="Weekends">Weekends</option>
                  <option value="Monday">Monday</option>
                  <option value="Tuesday">Tuesday</option>
                  <option value="Wednesday">Wednesday</option>
                  <option value="Thursday">Thursday</option>
                  <option value="Friday">Friday</option>
                  <option value="Saturday">Saturday</option>
                  <option value="Sunday">Sunday</option>
                </Field>
                {errors.repeat && touched.repeat && (
                  <div className="text-red-500 text-sm mt-1">
                    {errors.repeat}
                  </div>
                )}
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-2 text-white font-semibold rounded-md transition ${
                  isSubmitting
                    ? "bg-gray-500"
                    : "bg-yellow-600 hover:bg-yellow-700"
                }`}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            </Form>
          )}
        </Formik>
      </div>
      {successMessage && (
        <SuccessMessage
          message={successMessage}
          onClose={() => setSuccessMessage(null)}
        />
      )}
      {errorMessage && (
        <ErrorMessage
          message={errorMessage}
          onClose={() => setErrorMessage(null)}
        />
      )}
    </div>
  );
};

export default AddNotification;
